import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicOutOfStockModal } from '@/features/shop/components/Modal/OutOfStockModal/DynamicOutOfStockModal'
import {
  type OnProductCallback,
  type OnQtyCallback,
  type ProductCardProduct,
} from '@/features/shop/components/ProductCard/types'
import { getQtyIncrementForProduct } from '@/features/shop/components/ProductCard/Utils'
import { type UpdateProductQuantityFn } from '@/features/shared/hooks/useUpdateProductQuantity'
import { useFetchSubstitutionsQuery } from '@/features/shop/services/Substitutions/hooks'
import { SubstitutionForProductState } from '@/features/shop/services/Substitutions/types'
import { getIsProductPreference } from '@/features/shop/services/Substitutions/utils'
import isEmpty from 'lodash/isEmpty'
import { useState, useCallback } from 'react'

type Props = {
  index: number
  product: ProductCardProduct
  onQtyAdded: OnQtyCallback
  onQtyRemoved: OnProductCallback
  productQuantity: number
  updateProductQuantity: UpdateProductQuantityFn
}

export const useProductCardQuickAdd = ({
  index,
  product,
  onQtyAdded,
  onQtyRemoved,
  productQuantity,
  updateProductQuantity,
}: Props) => {
  const { openDialog } = useHandleDialog()
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false)
  const fetchSubstitutions = useFetchSubstitutionsQuery()

  const handleSubstitutions = useCallback(() => {
    return fetchSubstitutions({ productId: product.id }).then((data) => {
      const { substitutions, preferredSubstitution: preference, state } = data

      const isPreferenceUnavailable =
        (state !== SubstitutionForProductState.available && isEmpty(preference)) ||
        (getIsProductPreference(preference) && !preference.available)

      const shouldPromptForSubstitution =
        isEmpty(substitutions) || state === SubstitutionForProductState.lowStock

      if (isPreferenceUnavailable && shouldPromptForSubstitution) {
        openDialog(DynamicOutOfStockModal, { product })
        return true
      }
      return false
    })
  }, [fetchSubstitutions, openDialog, product])

  const handleAddQty = useCallback(async () => {
    // Run substitution check in parallel with cart mutation
    // Suppress free item promo modal if OOS takes priority
    const outOfStockCheck = product.lowStock ? handleSubstitutions() : Promise.resolve(false)
    setIsUpdatingQuantity(true)
    const quantityToAdd = getQtyIncrementForProduct(product)
    const itemUpdated = Boolean(productQuantity)
    const quantity = itemUpdated ? productQuantity + quantityToAdd : quantityToAdd

    await updateProductQuantity({
      product,
      quantity,
      itemUpdated,
    }).finally(() => setIsUpdatingQuantity(false))
    const shouldShowOutOfStockModal = await outOfStockCheck.catch(() => false)
    onQtyAdded({ product, itemUpdated, index })
    return shouldShowOutOfStockModal
  }, [handleSubstitutions, product, productQuantity, onQtyAdded, index, updateProductQuantity])

  const handleRemoveQty = useCallback(async () => {
    setIsUpdatingQuantity(true)
    const quantityToRemove = getQtyIncrementForProduct(product)
    const quantity = productQuantity - quantityToRemove

    await updateProductQuantity({
      product,
      quantity,
      itemUpdated: true,
    }).finally(() => setIsUpdatingQuantity(false))

    onQtyRemoved({ product, index })
  }, [product, productQuantity, updateProductQuantity, onQtyRemoved, index])

  return {
    handleAddQty,
    handleRemoveQty,
    isUpdatingQuantity,
  }
}
